import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, thumbUrl } from "../lib/api";
import Sidebar from "../components/Sidebar";
import PageTopBar from "../components/PageTopBar";
import RetryImage from "../components/RetryImage";
import { useAuth } from "../lib/auth";
import { Sparkles, ArrowRight } from "lucide-react";

export default function Home() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [lib, setLib] = useState([]);
    const [chats, setChats] = useState([]);
    const [stats, setStats] = useState({ total_images: 0, total_chats: 0 });

    useEffect(() => {
        api.get("/library").then((r) => setLib((r.data.items || []).slice(0, 12))).catch(() => {});
        api.get("/chats").then((r) => setChats(r.data.slice(0, 5))).catch(() => {});
        api.get("/catalog/usage").then((r) => setStats(r.data)).catch(() => {});
    }, []);

    useEffect(() => {
        const onBrand = () => {
            api.get("/library").then((r) => setLib((r.data.items || []).slice(0, 12))).catch(() => {});
            api.get("/chats").then((r) => setChats(r.data.slice(0, 5))).catch(() => {});
            api.get("/catalog/usage").then((r) => setStats(r.data)).catch(() => {});
        };
        window.addEventListener("kc:brand-changed", onBrand);
        return () => window.removeEventListener("kc:brand-changed", onBrand);
    }, []);

    const newChat = () => {
        // Lazy chat creation — the actual `chats` row is inserted on the
        // first message send (see Chat.jsx `send`). Clicking "Nuevo chat"
        // without typing anything would otherwise leave empty rows in
        // history that clutter the sidebar and the admin logs. This
        // matches the behaviour of <NewChatButton> and <Sidebar>'s CTA.
        window.dispatchEvent(new CustomEvent("kc:new-chat-requested"));
        navigate("/chats");
    };

    return (
        <div className="flex min-h-screen bg-ink-950">
            <Sidebar />
            <main className="flex-1 overflow-y-auto">
                <PageTopBar />
                <div className="max-w-6xl mx-auto px-10 py-12 space-y-14">
                    {/* hero */}
                    <section className="animate-fade-up">
                        <p className="font-mono text-[10px] tracking-[0.3em] text-fog-400 uppercase mb-4">
                            01 · Bienvenido{user?.name ? `, ${user.name.split(" ")[0]}` : ""}
                        </p>
                        <h1 className="font-display text-6xl lg:text-7xl leading-[1.02] text-fog-100 max-w-3xl">
                            Nuevas piezas con <em className="text-periwinkle-800 not-italic">memoria</em> de lo que ya construiste.
                        </h1>
                        <p className="text-fog-400 text-base leading-relaxed mt-6 max-w-xl">
                            Inicia un chat, adjunta referencias propias o externas, y deja que la IA organice el prompt con tu lenguaje de marca.
                        </p>
                        <div className="mt-10 flex flex-wrap items-center gap-4">
                            <button
                                data-testid="home-new-chat-btn"
                                onClick={newChat}
                                className="inline-flex items-center gap-3 rounded-full bg-periwinkle-500 hover:bg-periwinkle-600 text-ink-on px-6 py-3 text-sm font-medium transition-all shadow-glow-sm"
                            >
                                <Sparkles className="w-4 h-4" />
                                Nuevo chat
                                <ArrowRight className="w-4 h-4" />
                            </button>
                            <Stat label="Imágenes generadas" value={stats.total_images} />
                            <Stat label="Chats activos" value={stats.total_chats} />
                        </div>
                    </section>

                    <div className="editorial-divider h-px" />

                    {/* recent chats + library preview */}
                    <section className="grid lg:grid-cols-[1fr_1.4fr] gap-10">
                        <div>
                            <p className="font-mono text-[10px] tracking-[0.3em] text-fog-400 uppercase mb-4">
                                02 · Chats recientes
                            </p>
                            <div className="space-y-2">
                                {chats.length === 0 && (
                                    <p className="text-fog-500 text-sm font-mono">Sin chats aún.</p>
                                )}
                                {chats.map((c) => (
                                    <button
                                        key={c.id}
                                        onClick={() => navigate(`/chats/${c.id}`)}
                                        data-testid={`home-chat-${c.id}`}
                                        className="block w-full text-left rounded-xl border hairline border-ink-600 bg-ink-900/60 hover:border-periwinkle-500 hover:bg-ink-800 px-4 py-3 transition-all"
                                    >
                                        <p className="text-fog-100 text-sm truncate">{c.title}</p>
                                        <p className="font-mono text-[10px] text-fog-400 mt-1">
                                            {new Date(c.updated_at).toLocaleString()}
                                        </p>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <div className="flex items-baseline justify-between mb-4">
                                <p className="font-mono text-[10px] tracking-[0.3em] text-fog-400 uppercase">
                                    03 · Última biblioteca
                                </p>
                                <button
                                    onClick={() => navigate("/library")}
                                    className="font-mono text-[10px] uppercase tracking-widest text-periwinkle-800 hover:underline"
                                >
                                    Ver todo →
                                </button>
                            </div>
                            {lib.length === 0 ? (
                                <div className="rounded-2xl border hairline border-ink-600 border-dashed p-10 text-center">
                                    <p className="text-fog-400 text-sm">
                                        Aún no hay imágenes generadas. Empieza un chat.
                                    </p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-3 gap-3">
                                    {lib.map((it, i) => (
                                        <div
                                            key={it.id}
                                            className="rounded-xl overflow-hidden border hairline border-ink-600 group"
                                            style={{
                                                gridRow:
                                                    i % 5 === 0 ? "span 2" : undefined,
                                            }}
                                        >
                                            <RetryImage
                                                src={thumbUrl(it.storage_path, 320)}
                                                alt=""
                                                loading="lazy"
                                                className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </section>
                </div>
            </main>
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="rounded-xl border hairline border-ink-600 bg-ink-900/60 px-4 py-2.5">
            <p className="font-mono text-[10px] tracking-widest text-fog-400 uppercase">
                {label}
            </p>
            <p className="font-display text-2xl text-periwinkle-800 leading-none mt-1">
                {value}
            </p>
        </div>
    );
}
